<template>
    <div>
        <vs-tabs v-model="activeTab">
            <vs-tab label="List" icon-pack="feather" icon="icon-list">
                <div class="mt-6">
                    <template v-if="viewUsers">
                        <vs-row>
                            <vs-col vs-xs="12" vs-sm="10" vs-lg="10" class="px-0 pr-2 mb-base">
                                <vs-input
                                    class="w-full"
                                    placeholder="Type to Search"
                                    v-model="filters.search"
                                />
                            </vs-col>
                            <vs-col vs-xs="12" vs-sm="2" vs-lg="2" class="mb-base p-0 text-right">
                                <vx-tooltip text="Create New User" color="primary" class="inline-block">
                                    <vs-button color="primary" type="filled" @click="activeTab = 1; resetForm();" :disabled="loading">
                                        <font-awesome-icon :icon="addIcon" />
                                    </vs-button>
                                </vx-tooltip>
                                <vx-tooltip text="Refresh Data" color="primary" class="ml-2 inline-block">
                                    <vs-button color="primary" type="filled" @click="init()" :disabled="loading">
                                        <font-awesome-icon :icon="redoIcon" />
                                    </vs-button>
                                </vx-tooltip>
                            </vs-col>
                        </vs-row>
                        <div class="vx-col w-full mb-base">
                            <vx-card>
                                <vs-table :data="results" slot="no-body" :hoverFlat="true" stripe>
                                    <vs-tr>
                                        <vs-th>ID</vs-th>
                                        <vs-th>Name</vs-th>
                                        <vs-th>Role</vs-th>
                                        <vs-th>Active</vs-th>
                                        <vs-th>Last Login</vs-th>
                                        <vs-th>Created On</vs-th>
                                        <vs-th>Action</vs-th>
                                    </vs-tr>
                                    <template>
                                        <template v-for="(item, index) in results">
                                            <vs-tr :key="index">
                                                <vs-td>
                                                    {{ item.id }}
                                                </vs-td>
                                                <vs-td>
                                                    {{ item.first_name }} {{ item.last_name }}
                                                </vs-td>
                                                <vs-td>
                                                    {{ item.role ? item.role.role : '-' }}
                                                </vs-td>
                                                <vs-td>
                                                    {{ item.is_active ? 'Yes' : 'No'}}
                                                </vs-td>
                                                <vs-td>
                                                    <template v-if="item.last_login">
                                                        <FormatText :text="item.last_login" format="datetime" />
                                                    </template>
                                                    <template v-else>
                                                        -
                                                    </template>
                                                </vs-td>
                                                <vs-td>
                                                    <FormatText :text="item.created_on" format="datetime" />
                                                </vs-td>
                                                <vs-td>
                                                    <vs-button type="filled" size="small" color="primary" @click="editItem(item)">View/Edit</vs-button>
                                                </vs-td>
                                            </vs-tr>
                                        </template>
                                    </template>
                                </vs-table>
                            </vx-card>
                        </div>
                        <template v-if="!disable_pagination">
                            <vs-row v-if="total_results" class="mt-3 text-center">
                                <vs-pagination :total="total_pages" v-model="current_page" goto class="mx-auto"></vs-pagination>
                            </vs-row>
                        </template>
                    </template>
                    <template v-else>
                        <PermissionDenied />
                    </template>
                </div>
            </vs-tab>
            <vs-tab :label="editingId ? 'Edit User' : 'Add New User'" icon-pack="feather" :icon="editingId ? 'icon-edit' : 'icon-plus'">
                <div class="mt-6">
                    <template v-if="hasFormPermission"> 
                        <vx-card noShadow noRound>
                            <div class="mb-4">
                                <h3 class="mb-2">{{ editingId ? 'Edit User' : 'Create User' }}</h3>
                                <p class="text-muted mb-4" v-if="!editingId">Fill in the details below to create a new user entry.</p>
                                <p class="text-muted mb-4" v-else>Update the user details below.</p>
                            </div>
                            <vs-row>
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="6" class="mb-3 pr-2">
                                    <vs-input
                                        v-model="user.first_name"
                                        label="First Name"
                                        class="w-full"
                                        :readonly="!editPermission"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.first_name">
                                        {{ api_errors.first_name.join(', ') }}
                                    </small>
                                </vs-col>
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="6" class="mb-3">
                                    <vs-input
                                        v-model="user.last_name"
                                        label="Last Name"
                                        class="w-full"
                                        :readonly="!editPermission"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.last_name">
                                        {{ api_errors.last_name.join(', ') }}
                                    </small>
                                </vs-col>
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="6" class="mb-3 pr-2">
                                    <vs-input
                                        v-model="user.email"
                                        label="Email"
                                        class="w-full"
                                        type="Email"
                                        :disabled="Boolean(user.id)"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.email">
                                        {{ api_errors.email.join(', ') }}
                                    </small>
                                </vs-col>
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="6" class="mb-3">
                                    <vs-input
                                        v-model="user.password"
                                        label="Password"
                                        class="w-full"
                                        type="Password"
                                        :readonly="!editPermission"
                                        placeholder="Leave blank to keep current password"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.password">
                                        {{ api_errors.password.join(', ') }}
                                    </small>
                                </vs-col>
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="6" class="mb-3 pr-2">
                                    <SelectForeign
                                        :model.sync="user.role"
                                        label="Role"
                                        placeholder="Type to Search..."
                                        :multiple="false"
                                        :load_initial="true"
                                        :label_keys="['role']"
                                        :show_id_in_label="false"
                                        :endpoint="`${$this.$store.state.apiendpoints.role_list}?is_active=true`"
                                        container_class="w-full"
                                        input_class=""
                                        :use_id_as_value="false"
                                        :close_on_select="true"
                                        :disabled="!editPermission"
                                    />  
                                    <small class="block text-danger mt-1" v-if="api_errors.role">
                                        {{ api_errors.role }}
                                    </small>
                                </vs-col>
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="6" class="mb-3">
                                    <SelectForeign
                                        :model.sync="user.organisations"
                                        label="Organisation"
                                        placeholder="Choose Organisation..."
                                        :multiple="true"
                                        :load_initial="true"
                                        :label_keys="['name']"
                                        :show_id_in_label="false"
                                        :endpoint="`${$this.$store.state.apiendpoints.organisations}?is_active=true`"
                                        container_class="w-full"
                                        input_class=""
                                        :use_id_as_value="false"
                                        :close_on_select="false"
                                        :disabled="!editPermission"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.organisation || api_errors.organisation_ids">
                                        {{ api_errors.organisation || api_errors.organisation_ids }}
                                    </small>
                                </vs-col>
                                <vs-col vs-xs="12" vs-sm="12" vs-lg="12" class="mb-3">
                                    <label class="vs-input--label block mb-2">Is Active *</label>
                                    <div class="flex align-center">
                                        <vs-radio v-model="user.is_active" vs-name="is_active" :disabled="!editPermission" :vs-value="true" class="mr-4">Yes</vs-radio>
                                        <vs-radio v-model="user.is_active" vs-name="is_active" :disabled="!editPermission" :vs-value="false">No</vs-radio>
                                    </div>
                                    <small class="block text-danger mt-1" v-if="api_errors.is_active">
                                        {{ api_errors.is_active.join(', ') }}
                                    </small>
                                </vs-col>
                            </vs-row>

                            <vs-row class="mt-base">
                                <vs-col class="text-right">
                                    <vs-button @click="activeTab = 0; resetForm();" color="warning" type="flat" class="mr-2">Cancel</vs-button>
                                    <vs-button @click="save" :disabled="submit_disabled" v-if="editPermission" color="primary" type="filled">
                                        {{ editingId ? 'Update' : 'Save' }}
                                    </vs-button>
                                </vs-col>
                            </vs-row>
                        </vx-card>
                    </template>
                    <template v-else>
                        <PermissionDenied />
                    </template>
                </div>
            </vs-tab>
        </vs-tabs>
    </div>
</template>


<script>
import { faPlus, faRedo } from '@fortawesome/free-solid-svg-icons';
import {Request} from "../../utils/requests"

export default {
	props: {
        embedded_mode: {
            required: false,
            default: false
        },
        embedded_filters: {
            required: false,
            default: () => {}
        },
        disable_pagination: {
            required: false,
            default: false
        },
        global_mode: {
            required: false,
            type: Boolean,
            default: false
        }
    },
    data() {
        let offset = parseInt(this.$route.query.offset || 0);
        let limit = parseInt(this.$route.query.limit || 20);

        let current_page = Math.ceil((offset + limit) / limit);
        return  {
            results: [],

			next_url: null,
            loading: false,
            total_results: null,
            current_page: current_page,
            filters: {
                search: this.$route.query.search || null,
                ordering: this.$route.query.ordering || [],
                offset: offset,
                limit: limit
            },

            activeTab: 0,
            editingId: null,
            user: {
                email: null,
                password: null,
                first_name: null,
                last_name: null,
                role: {},
                organisations: [],
                is_active: true
            },
            submit_disabled: false,
            api_errors: {},
            organisationList: [],
        }
    },
    mounted() {
        this.init()
    },
	computed: {
        total_pages() {
            if(this.total_results == 0 || this.filters.limit == 0)  return 1
            return Math.ceil(this.total_results/this.filters.limit)
        },
        redoIcon: () => faRedo,
        addIcon: () => faPlus,
        viewUsers() { 
            return this.hasAccess('read', 'user', 'all')
        },
        createUser() {
            return this.hasAccess('create', 'user', 'all')
        },
        editUser() {
            return this.hasAccess('edit', 'user', 'all')
        },
        hasFormPermission() {
            return (!this.user.id && this.createUser) || (this.user.id && this.editUser) || (this.user.id && this.viewUsers)
        },
        editPermission() {
            return (!this.user.id && this.createUser) || (this.user.id && this.editUser)
        },
        $this() { return this; }
    },
    watch: {
        filters: {
            deep: true,
            handler(newV) {
                if (this.current_page == null) return;

                if (this.timer) clearTimeout(this.timer);

                this.timer = setTimeout(() => {
                    this.$router.push({ name: this.$route.name, query: {...newV, ...this.embedded_query_params} });
                }, 800);
            }
        },
        "$route.query"(newV) {
            this.load_data(newV);
        },
        current_page(newV, oldV) {
            if (newV == oldV) return;
            this.filters.offset = (newV - 1) * this.filters.limit;
        },
        organisationList(newList) {
            this.setOrganisationObjects();
        },
    },
    methods: {
        init(){
            this.load_data(this.filters)
        },
        calculate_page() {
            if (this.filters.offset == 0) this.current_page = 1;
            else
                this.current_page = Math.ceil(this.filters.offset / this.filters.limit);
        },
        load_data(filters=null) {
            this.loading = true;
            this.$vs.loading();

            let url = this.$store.state.apiendpoints.organisation_users

            let payload = filters

            if (!payload) payload = {};

            if (this.embedded_mode && this.embedded_filters) {
                payload = { ...payload, ...this.embedded_filters };
            }

            if (this.global_mode) {
                payload['global'] = true
            }

            this.api_errors = {}

            Request(this, 'GET', url, payload).then( 
                (res) => {
                    this.results = res.results
                    this.total_results = res.count
                    this.next_url = res.next;
                    this.loading = false;
                    this.$vs.loading.close();
                }, 
                (err) => {
                    this.loading = false;
                    this.$vs.loading.close();
                    if (err.error) {
                        this.api_errors = err.error

                        this.$vs.notify({
                            time: 2500,
                            title: 'Error',
                            text: "Please fix the errors and try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    } else {
                        this.$vs.notify({
                            time: 2500,
                            title: 'Error',
                            text: "Unable to load Users. Please try again later.",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    }
                }
            )
        },
        resetForm() {
            this.editingId = null
            this.user = {
                email: null,
                password: null,
                first_name: null,
                last_name: null,
                role: {},
                organisations: [],
                is_active: true
            }
            this.api_errors = {}
            this.organisationList = []
        },
        editItem(item) {
            this.editingId = item.id
            this.activeTab = 1
            this.load_user_data(item.id)
        },
        load_user_data(user_id) {
            let request_method = 'GET', url = this.$store.state.apiendpoints.organisation_user + user_id + "/"
            this.$vs.loading()
            Request(this, request_method, url).then(
                res => {
                    this.$vs.loading.close()
                    this.user = res
                    if (res.organisations && Array.isArray(res.organisations)) {
                        this.user.organisations = res.organisations;
                    }
                    this.getOrganisationsData();
                },
                err => {
                    this.$vs.loading.close()
                    this.$vs.notify({
                        time: 3000,
                        title: 'Error',
                        text: "An unknown error occurred! Please try again!",
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger'
                    })
                }
            )
        },
        getOrganisationsData() {
            let method = 'GET',
                url = this.$store.state.apiendpoints.organisations;
            Request(this, method, url, {limit: 1000}).then(res => {
                const results = res['results'];
                this.organisationList = results;
                this.setOrganisationObjects();
            }).catch(err => {
            });
        },
        setOrganisationObjects() {
            if (
                this.user &&
                this.user.organisations &&
                Array.isArray(this.organisationList) &&
                this.organisationList.length > 0
            ) {
                if (Array.isArray(this.user.organisations) && this.user.organisations.length > 0) {
                    if (typeof this.user.organisations[0] === 'number') {
                        const mapped = this.user.organisations.map(orgId => {
                            const found = this.organisationList.find(org => org.id === orgId);
                            if (found) {
                                return found;
                            } else {
                                return {
                                    id: orgId,
                                    name: `Organisation ${orgId} (Not Found)`,
                                    is_active: false
                                };
                            }
                        });
                        this.user.organisations = mapped;
                    }
                }
            }
        },
        save() {
            let request_method = 'POST', url = this.$store.state.apiendpoints.organisation_user
            if (this.editingId) {
                request_method = 'PUT'
                url += this.editingId + '/'
            }

            let payload = {
                ...this.user
            }
            
            if (payload.organisations && Array.isArray(payload.organisations)) {
                payload.organisation_ids = payload.organisations.map(org => {
                    return typeof org === 'object' && org.id ? org.id : org;
                }).filter(id => id != null);
                delete payload.organisations;
            }
            if (payload.organisation) {
                delete payload.organisation;
            }
            
            this.submit_disabled = true
            this.$vs.loading()

            Request(this, request_method, url, null, payload).then(
                res => {
                    this.$vs.loading.close()
                    this.submit_disabled = false
                    this.$vs.notify({
                        time: 3000,
                        title: 'Saved',
                        text: "Saved Successfully!",
                        iconPack: 'feather',
                        icon: 'icon-check-circle',
                        color: 'success'
                    })
                    this.resetForm()
                    this.activeTab = 0
                    this.init()
                },
                err => {
                    this.$vs.loading.close()
                    this.submit_disabled = false
                    if (err.error) {
                        this.api_errors = err.error
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error',
                            text: "Please fix the errors and try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    } else {
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error',
                            text: "An unknown error occurred! Please try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    }
                }
            )
        }
    }
}

</script>

<style lang="scss" scoped>
.text-muted {
    color: #6c757d;
    font-size: 0.9rem;
}
.align-center {
    align-items: center;
}
.mr-4 {
    margin-right: 1rem;
}
</style>
